import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';

const HeatmapFullscreenDialog = ({
  open,
  onClose,
  heatmapData,
  bgImage,
  hoveredZoneIdx,
  onZoneHover,
  HeatmapCanvasComponent,
}) => {
  const { t } = useTranslation('heatmap');
  const [fullscreenZoom, setFullscreenZoom] = useState(1);
  const [fullscreenPan, setFullscreenPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const handleZoomReset = () => {
    setFullscreenZoom(1);
    setFullscreenPan({ x: 0, y: 0 });
  };

  const handleClose = () => {
    handleZoomReset();
    if (onClose) onClose();
  };

  const handleZoomIn = () => {
    setFullscreenZoom(oldZoom => {
      const newZoom = Math.min(4, +(oldZoom + 0.25).toFixed(2));
      if (newZoom === oldZoom) return oldZoom;
      setFullscreenPan(oldPan => ({
        x: Math.round(oldPan.x * (newZoom / oldZoom)),
        y: Math.round(oldPan.y * (newZoom / oldZoom)),
      }));
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setFullscreenZoom(oldZoom => {
      const newZoom = Math.max(1, +(oldZoom - 0.25).toFixed(2));
      if (newZoom === oldZoom) return oldZoom;
      if (newZoom <= 1) {
        setFullscreenPan({ x: 0, y: 0 });
        return 1;
      }
      setFullscreenPan(oldPan => ({
        x: Math.round(oldPan.x * (newZoom / oldZoom)),
        y: Math.round(oldPan.y * (newZoom / oldZoom)),
      }));
      return newZoom;
    });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const { currentTarget, clientX, clientY } = e;
    const rect = currentTarget ? currentTarget.getBoundingClientRect() : null;

    setFullscreenZoom(oldZoom => {
      let newZoom = +(oldZoom * factor).toFixed(2);
      newZoom = Math.max(1, Math.min(4, newZoom));
      if (newZoom === oldZoom) return oldZoom;

      if (newZoom <= 1) {
        setFullscreenPan({ x: 0, y: 0 });
        return 1;
      }

      if (rect) {
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const dx = (clientX - rect.left) - cx;
        const dy = (clientY - rect.top) - cy;

        setFullscreenPan(oldPan => ({
          x: Math.round(oldPan.x - (dx - oldPan.x) * (newZoom / oldZoom - 1)),
          y: Math.round(oldPan.y - (dy - oldPan.y) * (newZoom / oldZoom - 1)),
        }));
      }

      return newZoom;
    });
  };

  const handleDoubleClick = (e) => {
    if (fullscreenZoom > 1) {
      handleZoomReset();
    } else {
      const { currentTarget, clientX, clientY } = e;
      const rect = currentTarget ? currentTarget.getBoundingClientRect() : null;
      const newZoom = 2;
      setFullscreenZoom(newZoom);
      if (rect) {
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const dx = (clientX - rect.left) - cx;
        const dy = (clientY - rect.top) - cy;
        setFullscreenPan({
          x: Math.round(-dx * (newZoom - 1)),
          y: Math.round(-dy * (newZoom - 1)),
        });
      } else {
        setFullscreenPan({ x: 0, y: 0 });
      }
    }
  };

  const handleMouseDown = (e) => {
    if (fullscreenZoom > 1) {
      setIsPanning(true);
      dragStartRef.current = { x: e.clientX - fullscreenPan.x, y: e.clientY - fullscreenPan.y };
    }
  };

  const handleMouseMovePan = (e) => {
    if (isPanning && fullscreenZoom > 1) {
      setFullscreenPan({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    }
  };

  const handleMouseUp = () => setIsPanning(false);

  const HeatmapCanvas = HeatmapCanvasComponent;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(8px)',
          backgroundImage: 'none',
          color: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }
      }}
    >
      {/* Floating Close Button */}
      <Tooltip title={t('canvas.close', 'Fechar')}>
        <IconButton
          onClick={handleClose}
          aria-label="close"
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1500,
            bgcolor: 'rgba(255, 255, 255, 0.90)',
            backdropFilter: 'blur(10px)',
            color: 'primary.main',
            border: '1.5px solid',
            borderColor: 'primary.main',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
            '&:hover': {
              bgcolor: 'primary.light',
              color: 'primary.dark',
              borderColor: 'primary.dark',
            },
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      {/* Floating Zoom Controls Bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1500,
          bgcolor: 'rgba(255, 255, 255, 0.90)',
          backdropFilter: 'blur(10px)',
          border: '1.5px solid',
          borderColor: 'primary.main',
          borderRadius: 6,
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.18)',
        }}
      >
        <Tooltip title={t('canvas.zoomOut', 'Diminuir Zoom')}>
          <span>
            <IconButton
              size="small"
              onClick={handleZoomOut}
              disabled={fullscreenZoom <= 1}
              sx={{
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                '&.Mui-disabled': { color: 'action.disabled' },
              }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Typography sx={{ color: 'primary.dark', fontSize: '0.8125rem', fontWeight: 800, minWidth: 44, textAlign: 'center', userSelect: 'none', letterSpacing: '0.02em' }}>
          {Math.round(fullscreenZoom * 100)}%
        </Typography>

        <Tooltip title={t('canvas.zoomIn', 'Aumentar Zoom')}>
          <span>
            <IconButton
              size="small"
              onClick={handleZoomIn}
              disabled={fullscreenZoom >= 4}
              sx={{
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                '&.Mui-disabled': { color: 'action.disabled' },
              }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(27, 117, 187, 0.3)', my: 0.5 }} />

        <Tooltip title={t('canvas.resetZoom', 'Repor Zoom')}>
          <span>
            <IconButton
              size="small"
              onClick={handleZoomReset}
              disabled={fullscreenZoom === 1 && fullscreenPan.x === 0 && fullscreenPan.y === 0}
              sx={{
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                '&.Mui-disabled': { color: 'action.disabled' },
              }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Heatmap Fullscreen Canvas Container with Zoom & Pan */}
      <Box
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMovePan}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: fullscreenZoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `scale(${fullscreenZoom}) translate(${fullscreenPan.x / fullscreenZoom}px, ${fullscreenPan.y / fullscreenZoom}px)`,
            transformOrigin: 'center center',
            transition: isPanning ? 'none' : 'transform 0.15s ease-out',
          }}
        >
          {heatmapData && HeatmapCanvas && (
            <HeatmapCanvas
              hotspots={heatmapData.hotspots}
              realZones={heatmapData.realZones}
              bgImage={bgImage}
              gridWidth={heatmapData.gridWidth}
              gridHeight={heatmapData.gridHeight}
              hoveredZoneIdx={hoveredZoneIdx}
              onZoneHover={onZoneHover}
              isFullScreen
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

HeatmapFullscreenDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  heatmapData: PropTypes.object,
  bgImage: PropTypes.instanceOf(HTMLImageElement),
  hoveredZoneIdx: PropTypes.number,
  onZoneHover: PropTypes.func,
  HeatmapCanvasComponent: PropTypes.elementType.isRequired,
};

export default HeatmapFullscreenDialog;
